import { Box, Card, CardContent, CircularProgress } from '@mui/material';
import { AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps';
import { format, parseISO } from 'date-fns';
import {
  Activity,
  ChevronDown,
  MapPin,
  Maximize2,
  Minimize2,
  Scale,
  Star,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { ReactNode } from 'react';

import { COLORS } from '@/components/dashboard/scoring/constants';
import { ScrollHintList } from '@/components/dashboard/scoring/ScrollOverflowHint';
import { Tip } from '@/components/dashboard/scoring/Tip';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq';
import { useNegotiationLeverage } from '@/hooks/issues/useNegotiationLeverage';
import { useMarketInsights, useSetCompStarred } from '@/hooks/useMarketInsights';
import {
  ComparableSale,
  ComponentStatus,
  MarketInsightsResponse,
  MarketTrendPoint,
} from '@/models/MarketInsights';
import { VALUE_FONT_SMALL } from '@/theme/consts';
import { formatLongDate } from '@/translations/formatters/date';
import { GOOGLE_MAPS_MAP_ID } from '@/types/maps';
import { computeFarPct, formatLotSize } from '@/utils/marketInsights';
import { toProperCase } from '@/utils/properCase';

const FONT = "'Manrope', sans-serif";
const AVM_COMP_VARIANCE_THRESHOLD_PCT = 15;
const AVM_COMP_MIN_STRONG_MATCHES = 3;
const STRONG_COMP_MATCH_SCORE = 80;

// Direct window.innerWidth + resize listener, NOT MUI's useMediaQuery — the
// latter has repeatedly failed on real iOS Safari in this app despite a
// matching viewport (see InspectionPunchList.tsx for the same pattern). 768
// matches this app's own theme breakpoint convention ("Full mobile layout
// below 768px", theme/breakpoints.ts).
const MOBILE_BREAKPOINT_PX = 768;
const useIsMobileWidth = (): boolean => {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200,
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width <= MOBILE_BREAKPOINT_PX;
};

// A real sale/AVM amount is never legitimately $0 — ATTOM returns 0 (or an
// empty string, already normalized to undefined upstream) for non-disclosed
// or distressed transfers. Render those as "Not disclosed" rather than a
// literal $0, which reads as a real price.
const formatCurrency = (value?: number, notDisclosedLabel = '—'): string =>
  value == null || value === 0
    ? notDisclosedLabel
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(value);

const formatCompact = (value?: number): string =>
  value == null || value === 0
    ? '—'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value);

// "Jan 2, 2026" — unambiguous (no MM/DD confusion) and a tighter fit for a
// compact comp row than the full month name used in the AVM section's "As
// of" line. Days-ago in parens gives a sense of recency at a glance.
const formatSoldDate = (saleDate: string): { label: string; daysAgo: string } => {
  const parsed = parseISO(saleDate.slice(0, 10));
  const daysAgo = Math.round((Date.now() - parsed.getTime()) / (1000 * 60 * 60 * 24));
  return { label: `Sold ${format(parsed, 'MMM d, yyyy')}`, daysAgo: `(${daysAgo}d)` };
};

// "2025" -> "'25" — mobile-only trend-chart x-axis tick, to give the chart
// a bit more breathing room on a narrow width.
const formatYearTick = (year: string): string => `'${year.slice(-2)}`;

// Matches GradeTag's exact pill convention (dashboard/scoring/GradeTag.tsx) —
// Exact values from the severity-count pills in HomeownerHub.tsx's Repair
// Planning card ("CRITICAL: 3" etc) — `${color}18` tint, uppercase, 10px.
const Pill = ({
  label,
  color,
  fontSize = 10,
  padding = '3px 10px',
  // Solid fill (dark text) instead of the usual faint tint — used only for
  // the comp-match "Strong match" tier, which wants to read as a stronger
  // badge, not the softer tint every other Pill caller on this page uses.
  solid = false,
  letterSpacing = '0.04em',
}: {
  label: string;
  color: string;
  fontSize?: number;
  padding?: string;
  solid?: boolean;
  letterSpacing?: string;
}) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 600,
      letterSpacing,
      lineHeight: 1,
      background: solid ? color : `${color}18`,
      color: solid ? 'var(--tx-text)' : color,
      borderRadius: 20,
      fontFamily: FONT,
      fontSize,
      padding,
    }}
  >
    {label}
  </span>
);

const Tag = ({ label }: { label: string }) => (
  <span
    style={{
      fontSize: 10,
      fontWeight: 600,
      color: COLORS.textDim,
      background: 'var(--tx-fill-2)',
      border: 0,
      borderRadius: 6,
      padding: '2px 7px',
      letterSpacing: '0.02em',
      fontFamily: FONT,
    }}
  >
    {label}
  </span>
);

const SectionCard = ({
  title,
  action,
  children,
}: {
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <Box sx={{ height: '100%' }}>
    <Card
      sx={{
        bgcolor: COLORS.card,
        border: 0,
        borderRadius: '25px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ p: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 0,
          }}
        >
          {/* Matches the app's real card-title convention (e.g. HomeownerHub's
              "Repair Planning" card) — not this file's original 22px/300 guess. */}
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 17, fontWeight: 300, color: 'var(--tx-text)', fontFamily: FONT, lineHeight: 1.2 }}>
            {title}
          </span>
          {action}
        </div>
        {children}
      </CardContent>
    </Card>
  </Box>
);

const StatusNotice = ({ status, label }: { status: ComponentStatus; label: string }) => {
  if (status === 'ready') return null;
  if (status === 'stale') {
    return (
      <div style={{ fontSize: 12, color: COLORS.textDim, marginBottom: 12, fontFamily: FONT }}>
        Showing last known {label.toLowerCase()} data — a fresh lookup is temporarily unavailable.
      </div>
    );
  }
  return (
    <div style={{ fontSize: 13, color: COLORS.textDim, fontFamily: FONT }}>
      {label} data is being prepared for this property. Check back shortly.
    </div>
  );
};

// ─── Estimated Value hero ───────────────────────────────────────────────

const REALISTIC_PRICE_COLOR = COLORS.critical;

// Thin low/high AVM range bar. The fill + dot mark the "effective" value —
// the plain AVM value normally, or the repair-adjusted (AVM minus repair
// credit) value when the toggle is on — and slide between the two so the
// movement itself reads as the credit being applied. The gap it slides
// through (repair-adjusted → original AVM position) gets shaded so that
// distance visually IS the credit amount, not just two disconnected ticks.
const ValueRangeBar = ({
  low,
  high,
  value,
  listPrice,
  band,
}: {
  low: number;
  high: number;
  value: number;
  // Plain white hash mark, no legend entry — hover for the value via the
  // native title tooltip, matching "no need to label it, just a hover tooltip".
  listPrice?: number;
  // `active` toggles visibility only — `band` itself stays mounted whenever a
  // repair-credit exists so the legend row's chip count (and therefore its
  // flex-wrap layout / the card's height) never changes when the toggle flips.
  band?: { from: number; to: number; color: string; label: string; active: boolean };
}) => {
  const pctFor = (n: number) => (high > low ? ((n - low) / (high - low)) * 100 : 50);
  const pct = pctFor(value);
  const bandFromPct = band != null ? Math.min(100, Math.max(0, pctFor(band.from))) : null;
  const bandToPct = band != null ? Math.min(100, Math.max(0, pctFor(band.to))) : null;
  const listPricePct = listPrice != null ? Math.min(100, Math.max(0, pctFor(listPrice))) : null;
  return (
    <div style={{ marginTop: 14 }}>
      <style>{`
        @keyframes avmDotPulseRing {
          0% { transform: scale(0.7); opacity: 0.7; }
          100% { transform: scale(2.4); opacity: 0; }
        }
      `}</style>
      <div
        style={{
          position: 'relative',
          height: 8,
          borderRadius: 6,
          background: 'var(--tx-fill-2)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: `${Math.min(100, Math.max(0, pct))}%`,
            borderRadius: 6,
            background: COLORS.accent2,
            transition: 'width 0.2s ease',
          }}
        />
        {/* Mounted only while active — this is absolutely positioned inside the
            bar's fixed-height track, so unmounting it never affects the card's
            height (unlike the legend chip below, which stays mounted). A
            purely opacity-based hide would leave the tooltip triggerable over
            an invisible area, which is the bug this avoids. */}
        {band != null && band.active && bandFromPct != null && bandToPct != null && (
          <Tip
            text={`${band.label}: ${formatCurrency(band.to - band.from)}`}
            style={{
              position: 'absolute',
              top: 0,
              height: '100%',
              left: `${Math.min(bandFromPct, bandToPct)}%`,
              width: `${Math.abs(bandToPct - bandFromPct)}%`,
              background: band.color,
              transition: 'left 0.2s ease, width 0.2s ease',
              borderBottom: 'none',
              cursor: 'default',
            }}
          />
        )}
        {listPricePct != null && (
          <Tip
            text={`List Price: ${formatCurrency(listPrice)}`}
            style={{
              position: 'absolute',
              top: -5,
              left: `calc(${listPricePct}% - 1px)`,
              width: 2,
              height: 18,
              background: 'var(--tx-glass-card)',
              borderRadius: 1,
              borderBottom: 'none',
              cursor: 'default',
            }}
          />
        )}
        <div
          style={{
            position: 'absolute',
            top: -2,
            left: `calc(${Math.min(100, Math.max(0, pct))}% - 6px)`,
            width: 12,
            height: 12,
            transition: 'left 0.2s ease',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: HOVER_COLOR,
              animation: 'avmDotPulseRing 1.8s ease-out infinite',
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: HOVER_COLOR,
              boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
            }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7 }}>
        <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>{formatCurrency(low)}</span>
        <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>{formatCurrency(high)}</span>
      </div>
      {band != null && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 5 }}>
          {/* Only shown when it states something the big hero number above
              DOESN'T already — i.e. only the repair-adjusted figure, and only
              while active. Always mounted (not conditionally unmounted)
              whenever a band exists so this never reflows the row's height. */}
          {band != null && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                visibility: band.active ? 'visible' : 'hidden',
              }}
            >
              <span style={{ width: 2, height: 12, borderRadius: 1, background: HOVER_COLOR }} />
              <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>
                Repair-adjusted {formatCurrency(value)}
              </span>
            </div>
          )}
          {/* Always mounted (not conditionally unmounted) whenever a band exists —
              only `visibility` toggles, so this chip never appears/disappears and
              reflows the legend row's height when the toggle flips. */}
          {band != null && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                visibility: band.active ? 'visible' : 'hidden',
              }}
            >
              <span style={{ width: 2, height: 12, borderRadius: 1, background: band.color }} />
              <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>
                {band.label} {formatCurrency(band.to - band.from)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Same track/thumb switch convention as PriceWaterfallChart's lever toggles.
const ToggleSwitch = ({
  on,
  onColor,
  onClick,
  label,
}: {
  on: boolean;
  onColor: string;
  onClick: () => void;
  label: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    onClick={onClick}
    style={{
      width: 36,
      height: 20,
      padding: 0,
      border: 0,
      borderRadius: 10,
      flexShrink: 0,
      cursor: 'pointer',
      background: on ? onColor : 'var(--tx-fill-3)',
      position: 'relative',
      transition: 'background 0.2s ease',
    }}
  >
    <div
      style={{
        position: 'absolute',
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: 'var(--tx-fill)',
        top: 3,
        left: on ? 19 : 3,
        transition: 'left 0.2s ease',
      }}
    />
  </button>
);

// ─── Market Dynamics narrative ───────────────────────────────────────────
// Hauser's directional heuristic, intentionally built from the three signals
// already available on this page. It is expected to evolve as additional
// market data sources become available; the reasoning face keeps today's
// contributing inputs visible rather than implying an opaque standard.

const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

// "up 4.2%" / "down 1.8%" / "flat" — shared phrasing for any signed rate.
const formatSignedPct = (pct: number, flatThreshold = 0.05): string => {
  if (Math.abs(pct) < flatThreshold) return 'flat';
  return `${pct >= 0 ? 'up' : 'down'} ${Math.abs(pct).toFixed(1)}%`;
};

interface MarketMomentum {
  direction: 'accelerating' | 'decelerating' | 'steady';
  recentGrowthPct: number;
  baselineGrowthPct: number;
  years: number;
  latestYear: string;
  volumeDirection: 'rising' | 'falling' | 'flat';
}

// Compares this year's actual price change against the trend's own longer-run
// average pace — "accelerating"/"decelerating" describe that comparison
// directly, not a claim about the broader market beyond what these two
// numbers show. The 2-point buffer around "steady" is our own choice (avoids
// calling normal noise a trend), not an industry threshold — same honesty
// standard as the comp-score heuristics elsewhere on this page.
const ACCELERATION_BUFFER_PTS = 2;
const VOLUME_FLAT_BUFFER_PTS = 5;

export const computeMarketMomentum = (points: MarketTrendPoint[]): MarketMomentum | null => {
  if (points.length < 2) return null;
  const ordered = [...points].sort((a, b) => a.intervalStart.localeCompare(b.intervalStart));
  const first = ordered[0];
  const prev = ordered[ordered.length - 2];
  const last = ordered[ordered.length - 1];
  if (first.medianSalePrice <= 0 || prev.medianSalePrice <= 0) return null;

  const recentGrowthPct = ((last.medianSalePrice - prev.medianSalePrice) / prev.medianSalePrice) * 100;
  const firstYear = Number.parseInt(first.intervalStart.slice(0, 4), 10);
  const lastYear = Number.parseInt(last.intervalEnd.slice(0, 4), 10);
  const years =
    Number.isFinite(firstYear) && Number.isFinite(lastYear) && lastYear > firstYear
      ? lastYear - firstYear
      : ordered.length - 1;
  const baselineGrowthPct = (Math.pow(last.medianSalePrice / first.medianSalePrice, 1 / years) - 1) * 100;
  const delta = recentGrowthPct - baselineGrowthPct;
  const direction: MarketMomentum['direction'] =
    delta > ACCELERATION_BUFFER_PTS ? 'accelerating' : delta < -ACCELERATION_BUFFER_PTS ? 'decelerating' : 'steady';

  const volumeChangePct =
    prev.homeSaleCount > 0 ? ((last.homeSaleCount - prev.homeSaleCount) / prev.homeSaleCount) * 100 : 0;
  const volumeDirection: MarketMomentum['volumeDirection'] =
    volumeChangePct > VOLUME_FLAT_BUFFER_PTS
      ? 'rising'
      : volumeChangePct < -VOLUME_FLAT_BUFFER_PTS
        ? 'falling'
        : 'flat';

  return {
    direction,
    recentGrowthPct,
    baselineGrowthPct,
    years,
    latestYear: last.intervalEnd.slice(0, 4),
    volumeDirection,
  };
};

interface MarketPositioning {
  listVsAvmPct: number | null;
  listVsCompsPct: number | null;
}

// "In line with" buffer is our own display choice (±1.5%), not a claim about
// what counts as meaningfully different in this market.
const POSITIONING_FLAT_BUFFER_PCT = 1.5;

const computeMarketPositioning = (
  listPrice: number | undefined,
  avmValue: number | undefined,
  comps: ComparableSale[],
): MarketPositioning => {
  const listVsAvmPct =
    listPrice != null && avmValue != null && avmValue > 0
      ? ((listPrice - avmValue) / avmValue) * 100
      : null;

  const disclosedCompPrices = comps
    .map((c) => c.salePrice)
    .filter((p): p is number => p != null && p > 0);
  const medianCompPrice = disclosedCompPrices.length > 0 ? median(disclosedCompPrices) : null;

  const listVsCompsPct =
    listPrice != null && medianCompPrice != null ? ((listPrice - medianCompPrice) / medianCompPrice) * 100 : null;

  return { listVsAvmPct, listVsCompsPct };
};

interface SaleVelocity {
  recentCount: number;
  totalCount: number;
}

const RECENT_SALE_WINDOW_DAYS = 90;

// Real, factual ratio — deliberately not labeled "fast"/"slow"/"typical"
// since there's no baseline pace for this specific area to compare against;
// the number speaks for itself rather than attaching an unsupported judgment.
const computeSaleVelocity = (comps: ComparableSale[]): SaleVelocity | null => {
  const withDates = comps.filter((c): c is ComparableSale & { saleDate: string } => Boolean(c.saleDate));
  if (withDates.length === 0) return null;
  const cutoff = Date.now() - RECENT_SALE_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const recentCount = withDates.filter((c) => new Date(c.saleDate).getTime() >= cutoff).length;
  return { recentCount, totalCount: withDates.length };
};

// Folds the three signals into one -1 (buyer-leaning) to +1 (seller-leaning)
// number to drive the gauge needle — an average of whichever sub-scores have
// data, not a weighted formula with any special claim to precision. Each
// sub-score's own scaling (e.g. "10% off-market = a full swing to one end")
// is our own display choice, same as the buffers above.
const computeCompositeLean = (
  momentum: MarketMomentum | null,
  positioning: MarketPositioning,
  velocity: SaleVelocity | null,
): number | null => {
  const parts: number[] = [];

  if (momentum) {
    const directionScore =
      momentum.direction === 'accelerating' ? 1 : momentum.direction === 'decelerating' ? -1 : 0;
    const volumeScore =
      momentum.volumeDirection === 'rising' ? 0.5 : momentum.volumeDirection === 'falling' ? -0.5 : 0;
    parts.push(Math.max(-1, Math.min(1, (directionScore + volumeScore) / 1.5)));
  }

  const positioningPct = positioning.listVsCompsPct ?? positioning.listVsAvmPct;
  if (positioningPct != null) {
    parts.push(Math.max(-1, Math.min(1, positioningPct / 10)));
  }

  if (velocity && velocity.totalCount > 0) {
    const ratio = velocity.recentCount / velocity.totalCount;
    parts.push((ratio - 0.5) * 2);
  }

  if (parts.length === 0) return null;
  return parts.reduce((a, b) => a + b, 0) / parts.length;
};

// lean is symmetric in [-1, 1], so scaling by the max alone covers the full
// [-58, 58] needle range — no separate min constant needed.
const MARKET_GAUGE_MAX_DEG = 58;

const MARKET_DYNAMICS_LABEL_STYLE = {
  fontSize: 12,
  fontWeight: 600,
  color: COLORS.textDim,
  fontFamily: FONT,
};

const MarketDynamicsGauge = ({ data, propertyId }: { data: MarketInsightsResponse; propertyId: string }) => {
  const { selectedProperty } = usePropertiesContext();
  const { propertyDetails } = useHauserEQResults(propertyId);
  const listPrice = propertyDetails?.price ?? selectedProperty?.property.price;
  const [isFlipped, setIsFlipped] = useState(false);

  const momentum = data.trends.status !== 'unavailable' ? computeMarketMomentum(data.trends.data) : null;
  const positioning = computeMarketPositioning(
    listPrice,
    data.avm.status !== 'unavailable' ? data.avm.data?.value : undefined,
    data.comparables.status !== 'unavailable' ? data.comparables.data : [],
  );
  const velocity = data.comparables.status !== 'unavailable' ? computeSaleVelocity(data.comparables.data) : null;
  const lean = computeCompositeLean(momentum, positioning, velocity);

  // Two short, parallel-structured sentences per column for the triptych —
  // same underlying numbers as the full sentences above, deliberately
  // rewritten to a matching two-clause shape (headline stat, then a second
  // real stat) so the three columns read as evenly balanced by length,
  // not just by having "some copy" under each icon.
  // One tight sentence, not two — kept to a similar length/line-count as
  // the other two columns. No redundant up/down prefix on the baseline
  // number ("above/below" already conveys direction on its own).
  const momentumSentences = momentum
    ? [
        (() => {
          const recent = formatSignedPct(momentum.recentGrowthPct);
          const capitalized = recent.charAt(0).toUpperCase() + recent.slice(1);
          const comparison = momentum.recentGrowthPct >= momentum.baselineGrowthPct ? 'above' : 'below';
          const baseline = Math.abs(momentum.baselineGrowthPct).toFixed(1);
          const volumeClause =
            momentum.volumeDirection !== 'flat' ? `, with volume ${momentum.volumeDirection}` : '';
          const period =
            momentum.latestYear === String(new Date().getFullYear())
              ? 'this year'
              : `in ${momentum.latestYear}`;
          return `${capitalized} ${period}, ${comparison} the ${baseline}%/yr average pace${volumeClause}.`;
        })(),
      ]
    : [];

  const positioningSentences = (() => {
    const parts: string[] = [];
    if (positioning.listVsCompsPct != null) {
      const p = positioning.listVsCompsPct;
      parts.push(
        Math.abs(p) < POSITIONING_FLAT_BUFFER_PCT
          ? 'Listed in line with nearby comps.'
          : `Listed ${Math.abs(p).toFixed(1)}% ${p >= 0 ? 'above' : 'below'} nearby comps.`,
      );
    }
    if (positioning.listVsAvmPct != null) {
      const p = positioning.listVsAvmPct;
      parts.push(
        Math.abs(p) < POSITIONING_FLAT_BUFFER_PCT
          ? 'In line with its estimated value.'
          : `Also ${Math.abs(p).toFixed(1)}% ${p >= 0 ? 'above' : 'below'} the estimated value.`,
      );
    }
    return parts;
  })();

  // Deliberately not framed as a percentage — the denominator here is our
  // own maxComps query limit (up to 20 comps we pulled), not an independent
  // count of every sale in the area. A "%" would read as a market-wide
  // statistic it isn't; "of the comps we're comparing against" keeps it
  // honest about what's actually being measured.
  const velocitySentences = velocity
    ? [`${velocity.recentCount} of the ${velocity.totalCount} comps we're comparing against sold in the last ${RECENT_SALE_WINDOW_DAYS} days.`]
    : [];

  // Primary big-number stat per column, shown under the icon+title lockup,
  // above the supporting sentences.
  const momentumStat = momentum ? formatSignedPct(momentum.recentGrowthPct) : null;
  const positioningStat =
    positioning.listVsCompsPct != null
      ? `${Math.abs(positioning.listVsCompsPct).toFixed(1)}%`
      : positioning.listVsAvmPct != null
        ? `${Math.abs(positioning.listVsAvmPct).toFixed(1)}%`
        : null;
  // Plain count, not a fraction/percentage of the comp pool — same reasoning
  // as the sentence above.
  const velocityStat = velocity ? `${velocity.recentCount}` : null;

  const width = 320;
  const height = 192;
  const cx = width / 2;
  const cy = height - 42;
  const r = 118;
  const strokeW = 18;

  const toXY = (angleDeg: number) => {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
  };
  const start = toXY(180);
  const end = toXY(0);
  const arcPath = `M ${start.x} ${start.y} A ${r} ${r} 0 0 1 ${end.x} ${end.y}`;

  const needleAngle = lean != null ? lean * MARKET_GAUGE_MAX_DEG : 0;
  const needleLen = r - strokeW - 12;
  const needleTip = { x: cx, y: cy - needleLen };

  // Matches the original gauge's natural height exactly (192px svg + its own
  // 6px marginTop, no caption anymore) — the flip mechanism needs an
  // explicit height (absolute-positioned faces), but it shouldn't be any
  // taller than the gauge alone ever was. The back face has its own
  // overflowY:auto as a safety net if its copy needs more room than this.
  const FLIP_CARD_HEIGHT = 198;

  const flipButton = (
    <button
      onClick={() => setIsFlipped((v) => !v)}
      aria-label={isFlipped ? 'Show market dynamics gauge' : 'Show what this gauge is based on'}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: CONTROL_HEIGHT,
        height: CONTROL_HEIGHT,
        flexShrink: 0,
        borderRadius: 8,
        background: 'var(--tx-fill)',
        color: COLORS.accent2,
        border: 0,
        outline: 'none',
        cursor: 'pointer',
      }}
    >
      <svg
        width={16}
        height={16}
        viewBox="0 0 24 24"
        fill="none"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M8 3H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h3" stroke={isFlipped ? COLORS.accent2 : 'var(--tx-text)'} />
        <path d="M16 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3" stroke={isFlipped ? 'var(--tx-text)' : COLORS.accent2} />
        <path d="M12 20v2" stroke={COLORS.accent2} />
        <path d="M12 14v2" stroke={COLORS.accent2} />
        <path d="M12 8v2" stroke={COLORS.accent2} />
        <path d="M12 2v2" stroke={COLORS.accent2} />
      </svg>
    </button>
  );

  return (
    <SectionCard title="Market dynamics" action={lean != null ? flipButton : undefined}>
      {lean == null ? (
        <span style={{ fontSize: 13, color: COLORS.textDim, fontFamily: FONT }}>
          Market dynamics data is being prepared for this property. Check back shortly.
        </span>
      ) : (
        <div style={{ perspective: 1200 }}>
          <div
            style={{
              position: 'relative',
              height: FLIP_CARD_HEIGHT,
              transformStyle: 'preserve-3d',
              transition: 'transform 0.6s',
              transform: isFlipped ? 'rotateY(180deg)' : 'none',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            {/* Front: the gauge */}
            <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <svg
                  width={width}
                  height={height}
                  viewBox={`0 0 ${width} ${height}`}
                  style={{ overflow: 'visible', display: 'block', marginTop: -14, maxWidth: '100%' }}
                >
                  <defs>
                    <linearGradient id="marketMeterGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={COLORS.accent1} />
                      <stop offset="50%" stopColor={COLORS.accent2} />
                      <stop offset="100%" stopColor={COLORS.critical} />
                    </linearGradient>
                  </defs>
                  <path
                    d={arcPath}
                    fill="none"
                    stroke="url(#marketMeterGrad)"
                    strokeWidth={strokeW}
                    strokeLinecap="round"
                  />
                  <g
                    style={{
                      transformOrigin: `${cx}px ${cy}px`,
                      transform: `rotate(${needleAngle}deg)`,
                      transition: 'transform 0.6s ease-in-out',
                    }}
                  >
                    <line
                      x1={cx}
                      y1={cy}
                      x2={needleTip.x}
                      y2={needleTip.y}
                      stroke={COLORS.text}
                      strokeWidth={3}
                      strokeLinecap="round"
                    />
                  </g>
                  <circle cx={cx} cy={cy} r={6} fill={COLORS.text} />
                  <text x={start.x} y={height - 6} fontSize={13} fill={COLORS.textDim} textAnchor="middle">
                    Buyer's Market
                  </text>
                  <text x={cx} y={cy - r - 20} fontSize={13} fill={COLORS.textDim} textAnchor="middle">
                    Neutral
                  </text>
                  <text x={end.x} y={height - 6} fontSize={13} fill={COLORS.textDim} textAnchor="middle">
                    Seller's Market
                  </text>
                </svg>
              </div>
            </div>

            {/* Back: the reasoning, one clearly-labeled block per signal */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
              }}
            >
              <div style={{ display: 'flex', flex: 1, alignItems: 'stretch', marginTop: 20 }}>
                {momentumSentences.length > 0 && momentum && momentumStat && (
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      textAlign: 'center',
                      padding: '0 20px',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                        {momentum.recentGrowthPct >= 0 ? (
                          <TrendingUp size={18} color={COLORS.accent2} strokeWidth={1.75} />
                        ) : (
                          <TrendingDown size={18} color={COLORS.accent2} strokeWidth={1.75} />
                        )}
                        <span style={MARKET_DYNAMICS_LABEL_STYLE}>Momentum</span>
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 600, color: COLORS.text, fontFamily: FONT, marginTop: 16 }}>
                        {momentumStat}
                      </div>
                    </div>
                    <div style={{ width: 24, height: 1, background: '#333333', margin: '16px auto 0' }} />
                    <div style={{ fontSize: 11, color: COLORS.textDim, fontFamily: FONT, lineHeight: 1.4, marginTop: 16 }}>
                      {momentumSentences.join(' ')}
                    </div>
                  </div>
                )}
                {positioningSentences.length > 0 && positioningStat && (
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      textAlign: 'center',
                      padding: '0 20px',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                        <Scale size={18} color={COLORS.accent2} strokeWidth={1.75} />
                        <span style={MARKET_DYNAMICS_LABEL_STYLE}>Position</span>
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 600, color: COLORS.text, fontFamily: FONT, marginTop: 16 }}>
                        {positioningStat}
                      </div>
                    </div>
                    <div style={{ width: 24, height: 1, background: '#333333', margin: '16px auto 0' }} />
                    <div style={{ fontSize: 11, color: COLORS.textDim, fontFamily: FONT, lineHeight: 1.4, marginTop: 16 }}>
                      {positioningSentences.join(' ')}
                    </div>
                  </div>
                )}
                {velocitySentences.length > 0 && velocityStat && (
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      textAlign: 'center',
                      padding: '0 20px',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                        <Activity size={18} color={COLORS.accent2} strokeWidth={1.75} />
                        <span style={MARKET_DYNAMICS_LABEL_STYLE}>Velocity</span>
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 600, color: COLORS.text, fontFamily: FONT, marginTop: 16 }}>
                        {velocityStat}
                      </div>
                    </div>
                    <div style={{ width: 24, height: 1, background: '#333333', margin: '16px auto 0' }} />
                    <div style={{ fontSize: 11, color: COLORS.textDim, fontFamily: FONT, lineHeight: 1.4, marginTop: 16 }}>
                      {velocitySentences.join(' ')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
};

const AvmSection = ({
  data,
  propertyId,
  isMobile,
}: {
  data: MarketInsightsResponse;
  propertyId: string;
  isMobile: boolean;
}) => {
  const avm = data.avm;
  const { selectedProperty } = usePropertiesContext();
  const { propertyDetails } = useHauserEQResults(propertyId);
  // Same precedence as NegotiationStrategyDialog/HomeownerHub — hauser-eq's
  // synced listing price over the raw Property record, since the latter is
  // often stale/unset while this is what's actually shown elsewhere in the app.
  const listPrice = propertyDetails?.price ?? selectedProperty?.property.price;
  const { leverageMax } = useNegotiationLeverage(propertyId);
  const [showRepairCredit, setShowRepairCredit] = useState(true);

  if (avm.status === 'unavailable' || !avm.data) {
    return (
      <SectionCard title="Estimated value">
        <StatusNotice status={avm.status} label="Valuation" />
      </SectionCard>
    );
  }

  const { value, low, high, confidence, eventDate } = avm.data;
  const compVarianceWarning = computeAvmCompVarianceWarning(
    value,
    data.subject ?? {},
    data.comparables.status === 'unavailable' ? [] : data.comparables.data,
  );
  const compVarianceTip = compVarianceWarning
    ? [
        `ATTOM's estimate is ${compVarianceWarning.variancePct.toFixed(1)}%`,
        `${compVarianceWarning.direction} the median of the three strongest`,
        `comparable sales (${formatCurrency(compVarianceWarning.compMedian)}).`,
        'Review the comparable evidence before relying on this estimate.',
      ].join(' ')
    : undefined;
  const hasLeverage = leverageMax > 0;
  const realisticPrice = value - leverageMax;
  const effectiveValue = hasLeverage && showRepairCredit ? realisticPrice : value;

  return (
    <SectionCard
      title="Estimated value"
      action={
        hasLeverage ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.textDim, fontFamily: FONT }}>
              Repair Burden
            </span>
            <ToggleSwitch
              on={showRepairCredit}
              onColor={REALISTIC_PRICE_COLOR}
              label="Include repair burden in the estimated value"
              onClick={() => setShowRepairCredit((v) => !v)}
            />
          </div>
        ) : undefined
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'flex-start',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              fontSize: 32,
              fontWeight: 600,
              letterSpacing: 0,
              lineHeight: 1,
              color: COLORS.text,
              fontFamily: VALUE_FONT_SMALL,
            }}
          >
            {formatCurrency(value)}
            {compVarianceWarning && (
              <Tip
                text={compVarianceTip}
                style={{ borderBottom: 'none', lineHeight: 1 }}
              >
                <sup
                  aria-label="Estimated value differs materially from comparable sales"
                  style={{
                    color: COLORS.critical,
                    fontFamily: FONT,
                    fontSize: 17,
                    fontWeight: 600,
                    lineHeight: 1,
                    marginLeft: 3,
                  }}
                >
                  *
                </sup>
              </Tip>
            )}
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 400,
              color: COLORS.textDim,
              fontFamily: FONT,
              marginTop: 7,
            }}
          >
            Range {formatCurrency(low)} – {formatCurrency(high)}
          </div>
        </div>

        {/* Always mounted (reserves its space) so the card's height never changes
            when the toggle flips — only visibility toggles. Same value/label
            styling as the hero AVM number + "Range" line on the left. Mobile
            gets a divider + left alignment instead of a mirrored right-aligned
            block, since stacked-and-right-aligned read as an ambiguous,
            disconnected second number rather than a related secondary figure. */}
        {hasLeverage && (
          <div
            style={{
              textAlign: isMobile ? 'left' : 'right',
              flexShrink: 0,
              visibility: showRepairCredit ? 'visible' : 'hidden',
              ...(isMobile && { borderTop: '1px solid #333333', paddingTop: 16, marginTop: 4 }),
            }}
          >
            <div
              style={{
                fontSize: 32,
                fontWeight: 600,
                letterSpacing: 0,
                lineHeight: 1,
                color: COLORS.text,
                fontFamily: VALUE_FONT_SMALL,
              }}
            >
              {formatCurrency(realisticPrice)}
            </div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 400,
                color: COLORS.textDim,
                fontFamily: FONT,
                marginTop: 7,
              }}
            >
              Repair Adjusted Value
            </div>
          </div>
        )}
      </div>

      <ValueRangeBar
        low={low}
        high={high}
        value={effectiveValue}
        listPrice={listPrice}
        band={
          hasLeverage
            ? {
                from: realisticPrice,
                to: value,
                color: REALISTIC_PRICE_COLOR,
                label: 'Repair credit',
                active: showRepairCredit,
              }
            : undefined
        }
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 'auto',
          paddingTop: 12,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>
          As of {formatLongDate(parseISO(eventDate), undefined, { hideWeekday: true })}.
        </span>
        <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>ATTOM Data</span>
        <Pill label={`${confidence}/100`} color={COLORS.accent2} />
      </div>
    </SectionCard>
  );
};

// ─── Comparable Sales ────────────────────────────────────────────────────

const SubjectPin = () => (
  <div
    style={{
      width: 18,
      height: 18,
      borderRadius: '50%',
      background: 'var(--tx-glass-panel)',
      backgroundImage: 'var(--tx-surface-light)',
      border: '3px solid var(--tx-line)',
      boxShadow: 'var(--tx-shadow)',
    }}
  />
);

// Neutral (not color-coded by any criteria yet) — GradeTag's exact
// "neutral" pairing: taupe fill, dark text.
const COMP_PIN_NEUTRAL_BG = COLORS.accent2;
const COMP_PIN_NEUTRAL_TEXT = '#262626';
const HOVER_COLOR = '#c0ff02';

// CompCard's top row mixes a 15px price, a 10px pill, and a 13px star icon —
// three different font-size/icon metrics that never reliably line up via
// plain alignItems alone (that's what kept looking misaligned). Giving the
// match-pill/star group and the price's first line the SAME explicit height
// (both centered within it) guarantees they share one true baseline.
const CONTROL_ROW_LINE_HEIGHT = 20;

export const CompPin = ({
  index,
  highlighted,
  onHover,
  onSelect,
  address,
}: {
  index: number;
  highlighted: boolean;
  onHover: (index: number | null) => void;
  onSelect: (index: number) => void;
  address?: string;
}) => (
  <button
    type="button"
    aria-label={`Comparable ${index + 1}${address ? `, ${toProperCase(address)}` : ''}: scroll to details`}
    onMouseEnter={() => onHover(index)}
    onMouseLeave={() => onHover(null)}
    onFocus={() => onHover(index)}
    onBlur={() => onHover(null)}
    onClick={() => onSelect(index)}
    style={{
      width: 18,
      height: 18,
      padding: 0,
      borderRadius: '50%',
      background: highlighted ? HOVER_COLOR : COMP_PIN_NEUTRAL_BG,
      color: COMP_PIN_NEUTRAL_TEXT,
      fontFamily: FONT,
      fontSize: 10,
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '2px solid var(--tx-line)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      cursor: 'pointer',
      transition: 'background 0.15s',
      outlineOffset: 2,
    }}
  >
    {index + 1}
  </button>
);

const COMPS_MAP_ID = 'market-insights-comps-map';
const LOT_ZOOM_LEVEL = 19;
// Shared height for the "Reset View" row above the map and its matching
// empty spacer above the list, so both columns' real content starts flush.
const RESET_VIEW_ROW_HEIGHT = 0;

// Imperatively pans/zooms the live map when zoomTarget changes (e.g. a
// comp's "zoom to lot" pin was clicked) — the Map component only takes
// defaultCenter/defaultZoom, so subsequent moves need the map instance
// itself, same pattern as NeighborhoodMapCard's MapTypeListener.
const ZoomTargetEffect = ({ target }: { target: { lat: number; lng: number } | null }) => {
  const map = useMap(COMPS_MAP_ID);
  useEffect(() => {
    if (!map || !target) return;
    map.panTo(target);
    map.setZoom(LOT_ZOOM_LEVEL);
    map.setMapTypeId('hybrid'); // satellite imagery + road/place labels
  }, [map, target]);
  return null;
};

// Tracks whether the active map type is satellite/hybrid (dark imagery) vs
// roadmap (light) so the control CSS below can flip icon/label color —
// same pattern as NeighborhoodMapCard's MapTypeListener.
const CompsMapTypeListener = ({ onChange }: { onChange: (isDark: boolean) => void }) => {
  const map = useMap(COMPS_MAP_ID);
  useEffect(() => {
    if (!map) return undefined;
    const update = () => {
      const t = map.getMapTypeId();
      onChange(t === 'satellite' || t === 'hybrid');
    };
    update();
    const listener = map.addListener('maptypeid_changed', update);
    return () => listener.remove();
  }, [map, onChange]);
  return null;
};

const CompsMap = ({
  subject,
  comps,
  hoveredIndex,
  onHoverIndex,
  onSelectIndex,
  zoomTarget,
}: {
  subject: { latitude: number; longitude: number };
  comps: ComparableSale[];
  hoveredIndex: number | null;
  onHoverIndex: (index: number | null) => void;
  onSelectIndex: (index: number) => void;
  zoomTarget: { lat: number; lng: number } | null;
}) => {
  const [isSatelliteView, setIsSatelliteView] = useState(false);
  const { selectedProperty } = usePropertiesContext();
  const propertyAddress = selectedProperty?.property.address ?? '';

  if (!import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
    return (
      <div
        style={{
          height: '100%',
          minHeight: 280,
          borderRadius: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ color: COLORS.textDim, fontSize: 12, fontFamily: FONT }}>
          Map unavailable: VITE_GOOGLE_MAPS_API_KEY not configured
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100%',
        minHeight: 280,
        borderRadius: 18,
        overflow: 'hidden',
      }}
    >
      <style>{`
        /* Light frosted glass + 10px radius on built-in Google Maps
           control buttons. Inner label/icon color flips by map type:
           dark (#333) on Map view, light (#f3f3f3) on Satellite.
           Pegman intentionally left as Google's default — overriding it
           with a CSS background broke its native drag-to-Street-View.
           Kept in lockstep with NeighborhoodMapCard's map control styling. */
        .gm-style .gm-style-mtc > button,
        .gm-style .gm-fullscreen-control,
        .gm-style .gm-svpc,
        .gm-style .gm-control-active {
          border-radius: 10px !important;
          background: rgba(255,255,255,0.25) !important;
          backdrop-filter: blur(12px) saturate(160%) !important;
          -webkit-backdrop-filter: blur(12px) saturate(160%) !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.18) !important;
          color: ${isSatelliteView ? '#f3f3f3' : '#333333'} !important;
        }
        .gm-style .gm-style-mtc > button {
          font-family: 'Manrope', sans-serif !important;
          font-size: 16px !important;
          font-weight: 400 !important;
          letter-spacing: -0.01em !important;
          text-transform: none !important;
        }
        /* Selected Map/Satellite button — solid dark fill with light label. */
        .gm-style .gm-style-mtc > button[aria-checked="true"],
        .gm-style .gm-style-mtc > button[aria-pressed="true"] {
          background: #333333 !important;
          color: #f3f3f3 !important;
        }
        .gm-style .gm-style-mtc ul li,
        .gm-style .gm-style-mtc ul li *,
        .gm-style .gm-style-mtc-menu * {
          font-family: 'Manrope', sans-serif !important;
        }
        .gm-style .gm-style-mtc > button svg,
        .gm-style .gm-fullscreen-control svg,
        .gm-style .gm-control-active svg {
          fill: ${isSatelliteView ? '#f3f3f3' : '#333333'} !important;
          stroke: ${isSatelliteView ? '#f3f3f3' : '#333333'} !important;
        }
        .gm-style .gm-fullscreen-control img,
        .gm-style .gm-control-active img {
          filter: ${isSatelliteView ? 'brightness(0) invert(1)' : 'brightness(0)'} !important;
        }
        /* Pegman: keep Google's native img (drag-to-Street-View intact) but
           desaturate the yellow figure to neutral grey, biased dark on Map
           view and light on Satellite view to match the icon flip. */
        .gm-style .gm-svpc img {
          filter: ${
            isSatelliteView ? 'grayscale(1) brightness(1.4)' : 'grayscale(1) brightness(0.6)'
          } !important;
        }
        /* 5px gap between the Map and Satellite buttons. */
        .gm-style .gm-style-mtc + .gm-style-mtc {
          margin-left: 5px !important;
        }
        /* Map type dropdown panel (Terrain checkbox menu) — same frosted
           glass + 10px radius + 11px label text as the parent buttons. */
        .gm-style .gm-style-mtc ul,
        .gm-style .gm-style-mtc-menu {
          border-radius: 10px !important;
          background: rgba(255,255,255,0.25) !important;
          backdrop-filter: blur(12px) saturate(160%) !important;
          -webkit-backdrop-filter: blur(12px) saturate(160%) !important;
          box-shadow: 0 4px 14px rgba(0,0,0,0.2) !important;
          padding: 4px !important;
          border: none !important;
          /* Align the dropdown's left edge with the parent button. */
          left: 0 !important;
          right: auto !important;
        }
        .gm-style .gm-style-mtc ul li,
        .gm-style .gm-style-mtc ul li *,
        .gm-style .gm-style-mtc-menu * {
          font-size: 12px !important;
          color: #333333 !important;
        }
        .gm-style .gm-style-mtc ul li {
          border-radius: 6px !important;
          display: flex !important;
          align-items: center !important;
          gap: 2.5px !important;
        }
        .gm-style .gm-style-mtc ul li > * + * {
          margin-left: 2.5px !important;
        }
      `}</style>
      <Map
        id={COMPS_MAP_ID}
        mapId={GOOGLE_MAPS_MAP_ID}
        defaultCenter={{ lat: subject.latitude, lng: subject.longitude }}
        defaultZoom={13.5}
        style={{ width: '100%', height: '100%' }}
        gestureHandling="greedy"
        disableDefaultUI={false}
      >
        <ZoomTargetEffect target={zoomTarget} />
        <CompsMapTypeListener onChange={setIsSatelliteView} />
        <AdvancedMarker position={{ lat: subject.latitude, lng: subject.longitude }}>
          <SubjectPin />
        </AdvancedMarker>
        {propertyAddress && (
          // Same address-label pill as NeighborhoodMapCard's ("The Area" page
          // map) — separate marker at the same position, lifted above the
          // black dot via marginBottom, exact same markup/styling.
          <AdvancedMarker position={{ lat: subject.latitude, lng: subject.longitude }}>
            <div
              style={{
                background: 'rgba(var(--tx-ink-rgb), 0.05)',
                borderRadius: '12px',
                padding: '6px 14px',
                marginBottom: 26,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-text)' }}>{propertyAddress}</span>
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: 'var(--tx-glass-panel)',
                  backgroundImage: 'var(--tx-surface-light)',
                  boxShadow: 'var(--tx-shadow)',
                  border: '1.5px solid var(--tx-line)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--tx-muted)',
                }}
              >
                H
              </div>
            </div>
          </AdvancedMarker>
        )}
        {comps.map((comp, i) => {
          if (comp.latitude == null || comp.longitude == null) return null;
          return (
            <AdvancedMarker key={comp.attomId ?? i} position={{ lat: comp.latitude, lng: comp.longitude }}>
              <CompPin
                index={i}
                highlighted={hoveredIndex === i}
                onHover={onHoverIndex}
                onSelect={onSelectIndex}
                address={comp.address}
              />
            </AdvancedMarker>
          );
        })}
      </Map>
    </div>
  );
};

// Comp match score — a single 0-100 blend of distance/recency/bed/bath/sqft
// fit + an arms-length penalty, all derived from fields we already pull (no
// new ATTOM calls). One score drives both the ring's % and its tier label,
// so there's only one source of truth to keep them from disagreeing.
const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

interface CompMatchSubject {
  beds?: number;
  baths?: number;
  sqFt?: number;
}

const computeCompMatchScore = (subject: CompMatchSubject, comp: ComparableSale): number | null => {
  if (comp.distanceMiles == null || !comp.saleDate) return null;

  // Recency window matches Fannie Mae Selling Guide B4-1.3-08's actual
  // standard (comps within the last 12 months), not an invented shorter one —
  // https://selling-guide.fanniemae.com/sel/b4-1.3-08/comparable-sales
  const daysSinceSale = (Date.now() - new Date(comp.saleDate).getTime()) / (1000 * 60 * 60 * 24);
  const recencyScore = clamp01(1 - daysSinceSale / 365); // 0 days=100%, 365+ days=0%
  // Fannie Mae sets no official mile limit — "best available comparable"
  // is the actual standard, and ~1mi is common suburban practice, not a rule.
  // Decay extends past that informal convention instead of already being at
  // 50% right at the distance appraisers routinely treat as normal.
  const distanceScore = clamp01(1 - comp.distanceMiles / 3); // 0mi=100%, 3mi+=0%
  // Bed/bath/sqft tolerance has no single published industry number (GLA
  // adjustments are explicitly "market-based judgment, no mandated %" per
  // appraisal-standards sourcing) — these three remain a labeled heuristic,
  // not a standards-derived figure like the two above.
  // Unknown subject/comp fields score neutral (0.7) rather than 0 — missing
  // data shouldn't be punished as harshly as a confirmed mismatch.
  const bedScore =
    subject.beds != null && comp.beds != null ? clamp01(1 - Math.abs(subject.beds - comp.beds) / 2) : 0.7;
  const bathScore =
    subject.baths != null && comp.baths != null
      ? clamp01(1 - Math.abs(subject.baths - comp.baths) / 2)
      : 0.7;
  const sqFtScore =
    subject.sqFt && comp.sqFt
      ? clamp01(1 - Math.abs(comp.sqFt - subject.sqFt) / subject.sqFt / 0.3) // 0%diff=100%, 30%+diff=0%
      : 0.7;
  const armsLengthMultiplier = comp.armsLength === false ? 0.85 : 1;

  const weighted =
    distanceScore * 0.3 + recencyScore * 0.2 + bedScore * 0.15 + bathScore * 0.15 + sqFtScore * 0.2;

  return Math.round(weighted * armsLengthMultiplier * 100);
};

export interface AvmCompVarianceWarning {
  compMedian: number;
  direction: 'above' | 'below';
  variancePct: number;
}

/**
 * Flags a material disagreement between ATTOM's AVM and the strongest
 * comparable evidence already shown on this page. A median keeps one unusual
 * transfer from controlling the warning, and requiring three 80+ match comps
 * prevents a sparse or weak comp pool from producing false precision.
 */
export const computeAvmCompVarianceWarning = (
  avmValue: number,
  subject: CompMatchSubject,
  comps: ComparableSale[],
): AvmCompVarianceWarning | null => {
  if (!Number.isFinite(avmValue) || avmValue <= 0) return null;

  const strongestPrices = comps
    .map((comp) => ({
      price: comp.salePrice,
      score: computeCompMatchScore(subject, comp),
    }))
    .filter(
      (comp): comp is { price: number; score: number } =>
        comp.price != null &&
        Number.isFinite(comp.price) &&
        comp.price > 0 &&
        comp.score != null &&
        comp.score >= STRONG_COMP_MATCH_SCORE,
    )
    .sort((a, b) => b.score - a.score)
    .slice(0, AVM_COMP_MIN_STRONG_MATCHES)
    .map((comp) => comp.price)
    .sort((a, b) => a - b);

  if (strongestPrices.length < AVM_COMP_MIN_STRONG_MATCHES) return null;

  const compMedian = strongestPrices[1];
  const signedVariancePct = ((avmValue - compMedian) / compMedian) * 100;
  const variancePct = Math.abs(signedVariancePct);

  if (variancePct <= AVM_COMP_VARIANCE_THRESHOLD_PCT) return null;

  return {
    compMedian,
    direction: signedVariancePct > 0 ? 'above' : 'below',
    variancePct,
  };
};

const compMatchTierLabel = (score: number): string =>
  score >= 80 ? 'Strong match' : score >= 55 ? 'Good match' : 'Relaxed match';

const compMatchTierColor = (score: number): string =>
  score >= 80 ? HOVER_COLOR : score >= 55 ? COLORS.accent2 : COLORS.textDim;

// Plain "XX%" pill (same Pill convention used for the assessed-value/
// confidence badges elsewhere on this page) — replaced the donut-ring
// version, which read as unclear rather than informative at a glance.
const CompMatchPill = ({
  score,
  subject,
  comp,
}: {
  score: number;
  subject: CompMatchSubject;
  comp: ComparableSale;
}) => {
  const color = compMatchTierColor(score);
  const daysSinceSale = comp.saleDate
    ? Math.round((Date.now() - new Date(comp.saleDate).getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const reasons = [
    comp.distanceMiles != null ? `${comp.distanceMiles.toFixed(2)}mi away` : null,
    daysSinceSale != null ? `sold ${daysSinceSale}d ago` : null,
    subject.beds != null && comp.beds != null
      ? comp.beds === subject.beds
        ? 'exact bed match'
        : `${Math.abs(subject.beds - comp.beds)} bed diff`
      : null,
    subject.sqFt && comp.sqFt
      ? `sqft ${(((comp.sqFt - subject.sqFt) / subject.sqFt) * 100).toFixed(0)}%`
      : null,
    comp.armsLength === false ? 'non-arms-length' : null,
  ]
    .filter((r): r is string => Boolean(r))
    .join(' · ');

  return (
    <Tip
      text={`${compMatchTierLabel(score)} (${score}%) — ${reasons}`}
      style={{
        borderBottom: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        // Small manual nudge — the pill's natural (padding-driven) box centers
        // slightly low next to the star/price, per visual check.
        transform: 'translateY(-2px)',
      }}
    >
      <Pill
        label={`${score}%`}
        color={color}
        solid={color === HOVER_COLOR}
        letterSpacing="0"
        fontSize={12}
      />
    </Tip>
  );
};

// Flat row separated by a hairline divider (not a boxed card) — the number
// badge and address both light up HOVER_COLOR in sync with the map pin.
// Click the row to expand more detail (lot size, floor-area ratio); click
// the pin icon specifically to zoom the map into that comp's lot instead.
const CompCard = ({
  comp,
  subject,
  index,
  highlighted,
  onHover,
  onZoomTo,
  onToggleStar,
  starPending,
  isLast,
  isExpanded,
  onToggleExpanded,
  isMobile,
  cardRef,
}: {
  comp: ComparableSale;
  subject: CompMatchSubject;
  index: number;
  highlighted: boolean;
  onHover: (index: number | null) => void;
  onZoomTo: (target: { lat: number; lng: number }) => void;
  onToggleStar: () => void;
  starPending: boolean;
  isLast: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  isMobile: boolean;
  cardRef?: (el: HTMLDivElement | null) => void;
}) => {
  // "Floor Area Ratio" is the closest standard real-estate term for
  // structure-size ÷ lot-size — this is a simple computed estimate from the
  // two ATTOM-reported figures, not a verified zoning-code FAR. Shared with
  // the FAR sort option so there's one formula, not two.
  const farPct = computeFarPct(comp);
  const matchScore = computeCompMatchScore(subject, comp);
  const soldInfo = comp.saleDate ? formatSoldDate(comp.saleDate) : null;

  // Match pill + star grouped deliberately — the score is the signal that
  // drives the decision to star a comp, so the action sits right next to
  // the number that justifies it. Inline in the top row on desktop; on
  // mobile it moves to its own row above the tags (see below) so the
  // address isn't fighting it for space and getting truncated to nothing.
  const matchAndStarGroup = (
    <div
      style={{
        height: CONTROL_ROW_LINE_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexShrink: 0,
      }}
    >
      {matchScore != null && (
        <span onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
          <CompMatchPill score={matchScore} subject={subject} comp={comp} />
        </span>
      )}
      {comp.attomId && (
        <button
          type="button"
          aria-label={comp.starred ? 'Unstar this comp' : 'Star this comp'}
          disabled={starPending}
          onClick={(e) => {
            e.stopPropagation();
            onToggleStar();
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: 0,
            border: 0,
            background: 'transparent',
            cursor: starPending ? 'default' : 'pointer',
            opacity: starPending ? 0.5 : 1,
          }}
        >
          <Star
            size={13}
            color={comp.starred ? '#f3f3f3' : COLORS.textDim}
            fill={comp.starred ? '#f3f3f3' : 'none'}
          />
        </button>
      )}
    </div>
  );

  return (
    <div
      ref={cardRef}
      onMouseEnter={() => onHover(index)}
      onMouseLeave={() => onHover(null)}
      onClick={onToggleExpanded}
      style={{
        padding: '15px 2px',
        borderBottom: isLast ? 'none' : '1px solid var(--tx-line)',
        display: 'flex',
        gap: 8,
        cursor: 'pointer',
      }}
    >
      {/* Index column: pin number — fixed width, matches the map pin numbering. */}
      <div style={{ display: 'flex', flexShrink: 0, paddingTop: 1 }}>
        <div
          style={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: highlighted ? HOVER_COLOR : COMP_PIN_NEUTRAL_BG,
            color: COMP_PIN_NEUTRAL_TEXT,
            fontSize: 12,
            fontWeight: 600,
            fontFamily: FONT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'background 0.15s',
          }}
        >
          {index + 1}
        </div>
      </div>

      {/* Content column: everything else, stacked */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          {/* Left: address + distance */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span
                style={{
                  fontFamily: FONT,
                  fontWeight: 600,
                  fontSize: 14,
                  letterSpacing: '-0.01em',
                  color: COLORS.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {comp.address ? toProperCase(comp.address) : 'Address unavailable'}
              </span>
              {comp.latitude != null && comp.longitude != null && (
                <button
                  type="button"
                  aria-label="Zoom map to this lot"
                  onClick={(e) => {
                    e.stopPropagation();
                    onZoomTo({ lat: comp.latitude as number, lng: comp.longitude as number });
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <MapPin size={12} color={HOVER_COLOR} />
                </button>
              )}
            </div>
            <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, marginTop: 2 }}>
              {comp.distanceMiles != null ? `${comp.distanceMiles.toFixed(2)} mi away` : '—'}
            </div>
          </div>

          {/* Middle: match score pill + star — desktop only inline; mobile
              renders matchAndStarGroup on its own row below instead, so the
              address isn't fighting it for space. */}
          {!isMobile && matchAndStarGroup}

          {/* Right: sale price + sold date — fixed width so "Not disclosed"
              (wider than most dollar amounts) can't push the ring out of
              alignment with rows above/below it. */}
          <div style={{ textAlign: 'right', minWidth: 130, flexShrink: 0, whiteSpace: 'nowrap' }}>
            {/* Fixed height + flex/alignItems:center, matching the middle
                group's own fixed height exactly — this is what actually
                guarantees pill+star+$ line up, regardless of the two
                different font sizes' own line-height/leading quirks (which
                is what was throwing off a plain alignItems:flex-start). */}
            <div
              style={{
                height: CONTROL_ROW_LINE_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                fontFamily: VALUE_FONT_SMALL,
                fontWeight: 600,
                fontSize: 17,
                letterSpacing: 0,
                color: COLORS.text,
              }}
            >
              {formatCurrency(comp.salePrice, 'Not disclosed')}
            </div>
            <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, marginTop: 2 }}>
              {soldInfo && (
                <>
                  {soldInfo.label} <span style={{ fontWeight: 600 }}>{soldInfo.daysAgo}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {isMobile && <div style={{ marginBottom: 6 }}>{matchAndStarGroup}</div>}

        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
          {[
            comp.beds ? `${comp.beds} bd` : null,
            comp.baths ? `${comp.baths} ba` : null,
            comp.sqFt ? `${comp.sqFt.toLocaleString()} sqft` : null,
            comp.yearBuilt ? `Built ${comp.yearBuilt}` : null,
          ]
            .filter((label): label is string => Boolean(label))
            .map((label) => (
              <Tag key={label} label={label} />
            ))}
          {comp.armsLength === false && <Pill label="Non-arm's" color={COLORS.urgent} />}
          <button
            type="button"
            aria-label={isExpanded ? 'Collapse comparable details' : 'Expand comparable details'}
            aria-expanded={isExpanded}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: 'auto',
              padding: 0,
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
              color: COLORS.textDim,
              transform: isExpanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s',
            }}
          >
            <ChevronDown size={12} />
          </button>
        </div>

        {isExpanded && (
          <div
            style={{
              marginTop: 2,
              display: 'flex',
              gap: 20,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>
                Lot size:{' '}
                <span style={{ color: 'var(--tx-text)' }}>{formatLotSize(comp.lotSizeSqFt)}</span>
              </div>
              <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>
                Floor area ratio (est.):{' '}
                <span style={{ color: 'var(--tx-text)' }}>
                  {farPct != null ? `${farPct.toFixed(1)}%` : 'Not available'}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginLeft: 20 }}>
              {comp.amenities && comp.amenities.length > 0 && (
                <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>
                  Amenities: <span style={{ color: 'var(--tx-text)' }}>{comp.amenities.join(', ')}</span>
                </div>
              )}
              <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>
                School District:{' '}
                <span style={{ color: 'var(--tx-text)' }}>{comp.schoolDistrict ?? 'Not available'}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Same row composition as CompCard (address/tags/expand-to-lot-size-FAR-
// amenities) so the subject property reads apples-to-apples against the
// comps directly below it — pinned above the list, not part of the
// sortable/rotating comp pool. Sale price + sold date (not meaningful for
// the subject) are swapped for List Price; match score/star are dropped
// entirely since there's nothing to match against.
const SubjectCard = ({
  subject,
  address,
  yearBuilt,
  listPrice,
  onZoomTo,
  isExpanded,
  onToggleExpanded,
}: {
  subject: NonNullable<MarketInsightsResponse['subject']>;
  address?: string;
  yearBuilt?: number;
  listPrice?: number;
  onZoomTo: (target: { lat: number; lng: number }) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}) => {
  const farPct = computeFarPct({ sqFt: subject.sqFt, lotSizeSqFt: subject.lotSizeSqFt });

  return (
    <div
      onClick={onToggleExpanded}
      style={{
        padding: '15px 2px',
        borderBottom: '1px solid var(--tx-line)',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, paddingTop: 1 }}>
          <SubjectPin />
          <div style={{ flex: 1, width: 2, minHeight: 0, background: HOVER_COLOR, marginTop: 4, borderRadius: 1 }} />
        </div>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span
                style={{
                  fontFamily: FONT,
                  fontWeight: 600,
                  fontSize: 14,
                  letterSpacing: '-0.01em',
                  color: COLORS.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {address || 'This property'}
              </span>
              <button
                type="button"
                aria-label="Center map on this property"
                onClick={(e) => {
                  e.stopPropagation();
                  onZoomTo({ lat: subject.latitude, lng: subject.longitude });
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  padding: 0,
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <MapPin size={12} color={HOVER_COLOR} />
              </button>
            </div>
            <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, marginTop: 2 }}>
              Subject property
            </div>
          </div>

          <div style={{ height: CONTROL_ROW_LINE_HEIGHT, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Pill label="Subject" color={COLORS.accent2} />
          </div>

          <div style={{ textAlign: 'right', minWidth: 130, flexShrink: 0, whiteSpace: 'nowrap' }}>
            <div
              style={{
                height: CONTROL_ROW_LINE_HEIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                fontFamily: VALUE_FONT_SMALL,
                fontWeight: 600,
                fontSize: 17,
                letterSpacing: 0,
                color: COLORS.text,
              }}
            >
              {formatCurrency(listPrice, 'Not disclosed')}
            </div>
            <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT, marginTop: 2 }}>
              List price
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
          {[
            subject.beds ? `${subject.beds} bd` : null,
            subject.baths ? `${subject.baths} ba` : null,
            subject.sqFt ? `${subject.sqFt.toLocaleString()} sqft` : null,
            yearBuilt ? `Built ${yearBuilt}` : null,
          ]
            .filter((label): label is string => Boolean(label))
            .map((label) => (
              <Tag key={label} label={label} />
            ))}
          <button
            type="button"
            aria-label={isExpanded ? 'Collapse subject property details' : 'Expand subject property details'}
            aria-expanded={isExpanded}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: 'auto',
              padding: 0,
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
              color: COLORS.textDim,
              transform: isExpanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s',
            }}
          >
            <ChevronDown size={12} />
          </button>
        </div>
        </div>
      </div>

      {isExpanded && (
        <div style={{ marginTop: 8, marginLeft: 26, display: 'flex', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>
              Lot size: <span style={{ color: 'var(--tx-text)' }}>{formatLotSize(subject.lotSizeSqFt)}</span>
            </div>
            <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>
              Floor area ratio (est.):{' '}
              <span style={{ color: 'var(--tx-text)' }}>
                {farPct != null ? `${farPct.toFixed(1)}%` : 'Not available'}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginLeft: 20 }}>
            {subject.amenities && subject.amenities.length > 0 && (
              <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>
                Amenities: <span style={{ color: 'var(--tx-text)' }}>{subject.amenities.join(', ')}</span>
              </div>
            )}
            <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: FONT }}>
              School District:{' '}
              <span style={{ color: 'var(--tx-text)' }}>{subject.schoolDistrict ?? 'Not available'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

type SortKey = 'distance' | 'price' | 'beds' | 'baths' | 'sqft' | 'lotsize' | 'far' | 'match' | 'dateSold';

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'distance', label: 'Distance' },
  { key: 'price', label: 'Price' },
  { key: 'beds', label: 'Beds' },
  { key: 'baths', label: 'Bath' },
  { key: 'sqft', label: 'Sq Ft' },
  { key: 'lotsize', label: 'Lot Size' },
  { key: 'far', label: 'FAR' },
  { key: 'match', label: 'Match %' },
  { key: 'dateSold', label: 'Date Sold' },
];

// Each field sorts in the direction that surfaces the most useful comps
// first (closest, highest price/size, best match, most recent) rather than
// exposing a separate asc/desc toggle for v1 — undisclosed/missing values
// always sort last, regardless of direction.
const SORT_ASCENDING: Record<SortKey, boolean> = {
  distance: true,
  price: false,
  beds: false,
  baths: false,
  sqft: false,
  lotsize: false,
  far: false,
  match: false,
  dateSold: false,
};

const sortValue = (
  comp: ComparableSale,
  key: SortKey,
  subject: CompMatchSubject,
): number | undefined => {
  switch (key) {
    case 'distance':
      return comp.distanceMiles;
    case 'price':
      return comp.salePrice || undefined; // 0 means non-disclosed — treat as missing
    case 'beds':
      return comp.beds;
    case 'baths':
      return comp.baths;
    case 'sqft':
      return comp.sqFt;
    case 'lotsize':
      return comp.lotSizeSqFt;
    case 'far':
      return computeFarPct(comp);
    case 'match':
      return computeCompMatchScore(subject, comp) ?? undefined;
    case 'dateSold':
      return comp.saleDate ? new Date(comp.saleDate).getTime() : undefined;
    default:
      return undefined;
  }
};

const sortComparables = (
  comps: ComparableSale[],
  key: SortKey,
  subject: CompMatchSubject,
): ComparableSale[] => {
  const ascending = SORT_ASCENDING[key];
  return [...comps].sort((a, b) => {
    const av = sortValue(a, key, subject);
    const bv = sortValue(b, key, subject);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return ascending ? av - bv : bv - av;
  });
};

// Control-row height/radius/border convention matched exactly from
// PropertiesPageNew's toolbar (inputStyle + its icon button): tonal fills, no strokes.
const CONTROL_HEIGHT = 40;

// Used on both breakpoints — 9 sort options as individual tabs read as
// clutter even on desktop, so a single dropdown replaces the tab row
// entirely. Same pill convention as PropertiesPageNew's `inputStyle` (40px
// height, 10px radius, bordered).
const SortDropdown = ({ value, onChange }: { value: SortKey; onChange: (key: SortKey) => void }) => (
  <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex' }}>
    {/* appearance:none strips the browser's native arrow — this replaces it
        so the control still reads as a dropdown, matching ChevronDown's use
        elsewhere in this file (e.g. CompCard's expand indicator). */}
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as SortKey)}
      aria-label="Sort comparable sales"
      style={{
        flex: 1,
        minWidth: 0,
        height: CONTROL_HEIGHT,
        boxSizing: 'border-box',
        padding: '0 34px 0 14px',
        borderRadius: 8,
        background: 'var(--tx-fill)',
        color: COLORS.accent2,
        border: 0,
        outline: 'none',
        fontSize: 13,
        fontWeight: 300,
        fontFamily: FONT,
        appearance: 'none',
        WebkitAppearance: 'none',
        cursor: 'pointer',
      }}
    >
      {SORT_OPTIONS.map((opt) => (
        <option key={opt.key} value={opt.key}>
          {opt.label}
        </option>
      ))}
    </select>
    <span
      style={{
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        color: COLORS.textDim,
        pointerEvents: 'none',
        display: 'flex',
      }}
    >
      <ChevronDown size={15} />
    </span>
  </div>
);

// All fetched comps (up to 20, see attomClient.ts DEFAULT_COMPS_PARAMS.maxComps)
// render in one sorted list — VISIBLE_ROWS_BEFORE_SCROLL is just the sizing
// hint for the scroll container below, not a hard display cap. The rest
// scroll into view instead of being rotated in/out, so nothing an advisor
// has already looked at disappears. Set to 4 so the total viewable rows
// (SubjectCard, which sits above this scroll region, + 4 comps) is 5.
const VISIBLE_ROWS_BEFORE_SCROLL = 4;
const APPROX_COMP_ROW_HEIGHT = 94;
const COMP_LIST_MAX_HEIGHT = VISIBLE_ROWS_BEFORE_SCROLL * APPROX_COMP_ROW_HEIGHT;
// Reserve the full subject + four-comp viewport on desktop even when a
// filter leaves only one or two comps. Without this floor, the row's natural
// height collapses and shrinks the map along with the list.
const COMPARABLES_DESKTOP_MIN_HEIGHT = APPROX_COMP_ROW_HEIGHT + COMP_LIST_MAX_HEIGHT;

const compKey = (comp: ComparableSale): string => comp.attomId ?? comp.address;

// Bordered square icon button — same 40x40/8px-radius/transparent-fill
// convention as PropertiesPageNew's "+" toolbar button, the one real
// reference the app has for this shape.
const StarredOnlyToggle = ({ active, onClick }: { active: boolean; onClick: () => void }) => (
  <Tip
    text={active ? 'Show all comps' : 'Show only starred comps'}
    style={{ borderBottom: 'none', display: 'flex' }}
  >
    <button
      onClick={onClick}
      aria-label={active ? 'Show all comps' : 'Show only starred comps'}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: CONTROL_HEIGHT,
        height: CONTROL_HEIGHT,
        flexShrink: 0,
        borderRadius: 8,
        background: active ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
        color: active ? 'var(--tx-text)' : COLORS.accent2,
        border: 0,
        outline: 'none',
        cursor: 'pointer',
      }}
    >
      <Star size={16} fill={active ? '#f3f3f3' : 'none'} />
    </button>
  </Tip>
);

// Same bordered 40x40 box as StarredOnlyToggle — toggles every currently
// visible comp between fully expanded and fully collapsed.
const ExpandAllButton = ({ allExpanded, onClick }: { allExpanded: boolean; onClick: () => void }) => (
  <Tip
    text={allExpanded ? 'Collapse all' : 'Expand all'}
    style={{ borderBottom: 'none', display: 'flex' }}
  >
    <button
      onClick={onClick}
      aria-label={allExpanded ? 'Collapse all comparable sales' : 'Expand all comparable sales'}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: CONTROL_HEIGHT,
        height: CONTROL_HEIGHT,
        flexShrink: 0,
        borderRadius: 8,
        background: 'var(--tx-fill)',
        color: COLORS.accent2,
        border: 0,
        outline: 'none',
        cursor: 'pointer',
      }}
    >
      {allExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
    </button>
  </Tip>
);

const ComparablesSection = ({
  data,
  propertyId,
  isMobile,
}: {
  data: MarketInsightsResponse;
  propertyId: string;
  isMobile: boolean;
}) => {
  const comparables = data.comparables;
  // Same list-price precedence as AvmSection — hauser-eq's synced listing
  // price over the raw Property record. Also used for the subject row's
  // address/year-built, since SubjectLocation (ATTOM) doesn't carry those.
  const { selectedProperty } = usePropertiesContext();
  const { propertyDetails } = useHauserEQResults(propertyId);
  const subjectAddress = selectedProperty?.property.address;
  const subjectYearBuilt = propertyDetails?.year_built ?? selectedProperty?.property.yearBuilt;
  const subjectListPrice = propertyDetails?.price ?? selectedProperty?.property.price;
  const [sortBy, setSortBy] = useState<SortKey>('distance');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [zoomTarget, setZoomTarget] = useState<{ lat: number; lng: number } | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [isSubjectExpanded, setIsSubjectExpanded] = useState(false);
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const setCompStarredMutation = useSetCompStarred(propertyId);

  // Keyed by originalIndex (matches the map pin numbering) so clicking a
  // numbered pin can scroll the matching row into view without needing
  // ScrollHintList itself to expose its scroll container.
  const compRowRefs = useRef(new globalThis.Map<number, HTMLDivElement>());
  const handleSelectIndex = (index: number) => {
    compRowRefs.current.get(index)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  // Full fetched pool, sorted — no more display-count rotation. The rest
  // scroll into view (see COMP_LIST_MAX_HEIGHT below) instead of cycling
  // in/out, so nothing an advisor has looked at disappears unexpectedly.
  const sorted = useMemo(
    () => sortComparables(comparables.data, sortBy, data.subject ?? {}),
    [comparables.data, sortBy, data.subject],
  );

  const visibleComps = useMemo(
    () => (showStarredOnly ? sorted.filter((c) => c.starred) : sorted),
    [sorted, showStarredOnly],
  );

  // Includes the subject row so "Expand all"/"Collapse all" covers it too,
  // not just the comps below it. Operates on whatever's currently visible
  // (starred-only or the full list), not the whole fetched pool.
  const allExpanded =
    (visibleComps.length > 0 || Boolean(data.subject)) &&
    visibleComps.every((c) => expandedKeys.has(compKey(c))) &&
    (!data.subject || isSubjectExpanded);
  const handleToggleAllExpanded = () => {
    const next = !allExpanded;
    setExpandedKeys(next ? new Set(visibleComps.map(compKey)) : new Set());
    setIsSubjectExpanded(next);
  };
  const handleToggleOneExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // useMap looks up the registered map instance by id from APIProvider's
  // shared context — works from here even though this isn't a descendant
  // of the <Map> itself, same context CompsMap's <Map id={COMPS_MAP_ID}>
  // registers into.
  const resetMapInstance = useMap(COMPS_MAP_ID);
  const handleResetView = () => {
    if (!resetMapInstance || !data.subject) return;
    resetMapInstance.panTo({ lat: data.subject.latitude, lng: data.subject.longitude });
    resetMapInstance.setZoom(13.5);
    resetMapInstance.setMapTypeId('roadmap');
  };

  const controlsRow = comparables.data.length > 0 ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: COLORS.textDim,
          fontFamily: FONT,
          flexShrink: 0,
        }}
      >
        Sort:
      </span>
      <SortDropdown value={sortBy} onChange={setSortBy} />
      <StarredOnlyToggle active={showStarredOnly} onClick={() => setShowStarredOnly((v) => !v)} />
      <ExpandAllButton allExpanded={allExpanded} onClick={handleToggleAllExpanded} />
    </div>
  ) : undefined;

  return (
    <SectionCard
      title="Comparable sales"
      // On mobile the controls get their own row below the title (rendered
      // as the first child below) instead of squeezing into the title row —
      // desktop keeps them inline via SectionCard's action slot, unchanged.
      action={isMobile ? undefined : controlsRow}
    >
      {isMobile && controlsRow && <div style={{ marginBottom: 12 }}>{controlsRow}</div>}
      <StatusNotice status={comparables.status} label="Comparable sales" />
      {comparables.status !== 'unavailable' &&
        (sorted.length === 0 ? (
          <span style={{ fontSize: 13, color: COLORS.textDim, fontFamily: FONT }}>
            No comparable sales found nearby that match the unique footprint of this home.
          </span>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '20px' : '40px',
              ...(!isMobile && { minHeight: COMPARABLES_DESKTOP_MIN_HEIGHT }),
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              {/* Fixed-height header strip so the map's top edge lines up with
                  the list's first row — the list gets a matching empty
                  spacer below instead of the map just being pushed down. */}
              <div
                style={{
                  height: RESET_VIEW_ROW_HEIGHT,
                  marginBottom: 0,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                {data.subject && (
                  <button
                    onClick={handleResetView}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 7,
                      background: 'var(--tx-fill)',
                      color: COLORS.textDim,
                      fontSize: 10,
                      fontWeight: 600,
                      border: 'none',
                      outline: 'none',
                      cursor: 'pointer',
                      fontFamily: FONT,
                      // Purely visual lift — transform doesn't affect layout
                      // flow, so this doesn't push/shift anything else.
                      transform: 'translateY(-18px)',
                    }}
                  >
                    Reset View
                  </button>
                )}
              </div>
              {/* flex:1 + minHeight:0 fills the remaining space when this
                  column sits in a row (desktop) — but on mobile it's the
                  only content in its own stacked column, no sibling row to
                  borrow height from, so flex:1 has nothing to grow into and
                  the map never gets a real size (this is what was rendering
                  as a blank box). Mobile gets an explicit fixed height
                  instead, known synchronously at mount, matching the
                  existing 280px floor CompsMap already used as a minimum. */}
              <Box sx={isMobile ? { height: 280 } : { flex: 1, minHeight: 0 }}>
                {data.subject ? (
                  <CompsMap
                    subject={data.subject}
                    comps={visibleComps}
                    hoveredIndex={hoveredIndex}
                    onHoverIndex={setHoveredIndex}
                    onSelectIndex={handleSelectIndex}
                    zoomTarget={zoomTarget}
                  />
                ) : (
                  <div
                    style={{
                      height: '100%',
                      minHeight: 280,
                      borderRadius: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 16,
                      textAlign: 'center',
                    }}
                  >
                    <span style={{ color: COLORS.textDim, fontSize: 12, fontFamily: FONT }}>
                      Map unavailable — this property's location data hasn't loaded yet.
                    </span>
                  </div>
                )}
              </Box>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={{ height: RESET_VIEW_ROW_HEIGHT, marginBottom: 0, flexShrink: 0 }} />
              {data.subject && (
                <SubjectCard
                  subject={data.subject}
                  address={subjectAddress}
                  yearBuilt={subjectYearBuilt}
                  listPrice={subjectListPrice}
                  onZoomTo={setZoomTarget}
                  isExpanded={isSubjectExpanded}
                  onToggleExpanded={() => setIsSubjectExpanded((v) => !v)}
                />
              )}
              {/* Fixed max-height (~6 comp rows) with internal scroll — the
                  rest of the up-to-20 pool scrolls into view instead of
                  rotating in/out, so nothing already looked at disappears.
                  Starred comps stay reachable via the "starred only" filter
                  even if scrolled out of the visible window. Scrollbar
                  hidden (same convention as RfqHistoryDialog/InspectionPunchList)
                  — ScrollHintList's bouncing chevron communicates overflow instead. */}
              {showStarredOnly && visibleComps.length === 0 ? (
                <span
                  style={{
                    paddingTop: 15,
                    fontSize: 13,
                    color: COLORS.textDim,
                    fontFamily: FONT,
                  }}
                >
                  No comps starred yet — star a few from the full list to focus this view.
                </span>
              ) : (
                <>
                  <style>{`.market-insights-comps-scroll::-webkit-scrollbar{width:0;background:transparent}.market-insights-comps-scroll{scrollbar-width:none}`}</style>
                  <ScrollHintList
                    className="market-insights-comps-scroll"
                    style={{ maxHeight: COMP_LIST_MAX_HEIGHT, overflowY: 'auto' }}
                  >
                    {visibleComps.map((comp, i) => {
                      const originalIndex = visibleComps.indexOf(comp);
                      return (
                        <CompCard
                          key={compKey(comp)}
                          comp={comp}
                          subject={data.subject ?? {}}
                          index={originalIndex}
                          highlighted={hoveredIndex === originalIndex}
                          onHover={setHoveredIndex}
                          onZoomTo={setZoomTarget}
                          cardRef={(el) => {
                            if (el) compRowRefs.current.set(originalIndex, el);
                            else compRowRefs.current.delete(originalIndex);
                          }}
                          onToggleStar={() =>
                            setCompStarredMutation.mutate({
                              attomId: compKey(comp),
                              starred: !comp.starred,
                            })
                          }
                          starPending={
                            setCompStarredMutation.isPending &&
                            setCompStarredMutation.variables?.attomId === compKey(comp)
                          }
                          isLast={i === visibleComps.length - 1}
                          isExpanded={expandedKeys.has(compKey(comp))}
                          onToggleExpanded={() => handleToggleOneExpanded(compKey(comp))}
                          isMobile={isMobile}
                        />
                      );
                    })}
                  </ScrollHintList>
                </>
              )}
            </Box>
          </Box>
        ))}
    </SectionCard>
  );
};

// ─── Market Trends ───────────────────────────────────────────────────────

const TrendTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload: MarketTrendPoint }[];
}) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div
      style={{
        background: 'rgba(var(--tx-ink-rgb), 0.05)',
        borderRadius: '12px',
        padding: '8px 12px',
        fontFamily: FONT,
      }}
    >
      <div style={{ fontSize: 11, color: COLORS.text, fontWeight: 600 }}>{point.intervalStart}</div>
      <div style={{ fontSize: 11, color: COLORS.accent2 }}>{formatCurrency(point.medianSalePrice)} median</div>
      <div style={{ fontSize: 11, color: COLORS.textDim }}>{point.homeSaleCount} sales</div>
    </div>
  );
};

type TrendRange = '5y' | '10y';

const RANGE_OPTIONS: { key: TrendRange; label: string }[] = [
  { key: '5y', label: '5Y' },
  { key: '10y', label: '10Y' },
];

// Same segmented-pill pattern used elsewhere on this page (e.g. CompCard's tags).
const RangeToggle = ({ value, onChange }: { value: TrendRange; onChange: (range: TrendRange) => void }) => (
  <div style={{ display: 'flex', background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: 3, gap: 2 }}>
    {RANGE_OPTIONS.map((opt) => {
      const active = value === opt.key;
      return (
        <button
          key={opt.key}
          onClick={() => onChange(opt.key)}
          style={{
            padding: '4px 9px',
            borderRadius: 7,
            background: active ? 'var(--tx-fill-3)' : 'transparent',
            color: active ? COLORS.text : COLORS.textDim,
            fontSize: 10,
            fontWeight: 600,
            border: 'none',
            outline: 'none',
            cursor: 'pointer',
            fontFamily: FONT,
            transition: 'background 0.15s',
          }}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const TrendsSection = ({ data, isMobile }: { data: MarketInsightsResponse; isMobile: boolean }) => {
  const trends = data.trends;
  const [range, setRange] = useState<TrendRange>('10y');
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);
  const points = useMemo(
    () => trends.data.slice(-(range === '5y' ? 5 : 10)),
    [trends.data, range],
  );
  const first = points[0];
  const last = points[points.length - 1];
  const yoyChange =
    first && last && first.medianSalePrice > 0
      ? ((last.medianSalePrice - first.medianSalePrice) / first.medianSalePrice) * 100
      : null;

  const trendsControlsRow =
    trends.status !== 'unavailable' ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {yoyChange != null && (
          <Pill
            label={`${yoyChange >= 0 ? '+' : ''}${yoyChange.toFixed(1)}% since ${first.intervalStart}`}
            color={COLORS.accent2}
            fontSize={11}
            padding="5px 13px"
          />
        )}
        <RangeToggle value={range} onChange={setRange} />
      </div>
    ) : undefined;

  return (
    <SectionCard
      title={
        <>
          Market trends
          {trends.geography?.name && (
            <Pill label={trends.geography.name} color={COLORS.accent2} fontSize={11} padding="5px 13px" />
          )}
        </>
      }
      // Mobile: just the 5Y/10Y toggle stays on the title row (top-right,
      // aligned with "Market Trends"/zip) — the %-pill moves to its own
      // right-justified row below instead of both riding together. Desktop
      // keeps the combined row inline via the action slot, unchanged.
      action={isMobile ? (trends.status !== 'unavailable' ? <RangeToggle value={range} onChange={setRange} /> : undefined) : trendsControlsRow}
    >
      {isMobile && trends.status !== 'unavailable' && yoyChange != null && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: -8 }}>
          <Pill
            label={`${yoyChange >= 0 ? '+' : ''}${yoyChange.toFixed(1)}% since ${first.intervalStart}`}
            color={COLORS.accent2}
            fontSize={11}
            padding="5px 13px"
          />
        </div>
      )}
      <StatusNotice status={trends.status} label="Market trend" />
      {trends.status !== 'unavailable' && points.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '24px' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: COLORS.textDim, fontFamily: FONT, marginBottom: 6 }}>
              Median Sale Price
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart
                data={points}
                // Hiding the YAxis on mobile (width:0) removes the buffer
                // space it used to provide the leftmost dot/line — without
                // it the point sat right at the SVG edge and got clipped.
                // A small left margin restores that safety without wasting
                // width the way the full-size axis did.
                // Kept identical between both charts on purpose (see below) —
                // 4px was enough for the dot/line clipping fix but not for
                // the tick text: a 3-char "'17" label is center-anchored on
                // its tick's x-position, so its first/last glyph extends
                // ~10px past that center on a narrow mobile width, clipping
                // the apostrophe on the left and the last digit on the right.
                margin={{ top: 8, right: isMobile ? 14 : 8, left: isMobile ? 14 : 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.accent2} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={COLORS.accent2} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#333333" vertical={false} />
                <XAxis
                  dataKey="intervalStart"
                  tickFormatter={isMobile ? formatYearTick : undefined}
                  // Recharts' default tick-thinning is computed per chart
                  // type (categorical bar bands vs. line points), so the
                  // Area and Bar charts here — same data, same width — could
                  // silently drop different ticks (e.g. one keeping '17,
                  // the other not). interval={0} forces every year to
                  // render on both, consistently.
                  interval={0}
                  tick={{ fill: COLORS.textDim, fontSize: 11, fontFamily: FONT }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => formatCompact(v as number)}
                  tick={{ fill: COLORS.textDim, fontSize: 11, fontFamily: FONT }}
                  axisLine={false}
                  tickLine={false}
                  width={isMobile ? 0 : 48}
                  hide={isMobile}
                />
                <RechartsTooltip content={<TrendTooltip />} />
                <Area
                  type="monotone"
                  dataKey="medianSalePrice"
                  stroke={COLORS.accent2}
                  strokeWidth={2}
                  fill="url(#trendFill)"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: COLORS.textDim, fontFamily: FONT, marginBottom: 6 }}>
              Homes Sold
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart
                data={points}
                // Kept identical between both charts on purpose (see below) —
                // 4px was enough for the dot/line clipping fix but not for
                // the tick text: a 3-char "'17" label is center-anchored on
                // its tick's x-position, so its first/last glyph extends
                // ~10px past that center on a narrow mobile width, clipping
                // the apostrophe on the left and the last digit on the right.
                margin={{ top: 8, right: isMobile ? 14 : 8, left: isMobile ? 14 : 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#333333" vertical={false} />
                <XAxis
                  dataKey="intervalStart"
                  tickFormatter={isMobile ? formatYearTick : undefined}
                  // Recharts' default tick-thinning is computed per chart
                  // type (categorical bar bands vs. line points), so the
                  // Area and Bar charts here — same data, same width — could
                  // silently drop different ticks (e.g. one keeping '17,
                  // the other not). interval={0} forces every year to
                  // render on both, consistently.
                  interval={0}
                  tick={{ fill: COLORS.textDim, fontSize: 11, fontFamily: FONT }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: COLORS.textDim, fontSize: 11, fontFamily: FONT }}
                  axisLine={false}
                  tickLine={false}
                  width={isMobile ? 0 : 32}
                  allowDecimals={false}
                  hide={isMobile}
                />
                <RechartsTooltip content={<TrendTooltip />} cursor={false} />
                <Bar
                  dataKey="homeSaleCount"
                  radius={[4, 4, 0, 0]}
                  activeBar={false}
                  onMouseEnter={(_, index) => setHoveredBarIndex(index)}
                  onMouseLeave={() => setHoveredBarIndex(null)}
                >
                  {points.map((point, i) => (
                    <Cell
                      key={point.intervalStart}
                      fill={hoveredBarIndex === i ? HOVER_COLOR : COLORS.accent2}
                      fillOpacity={hoveredBarIndex === i ? 1 : 0.85}
                      style={{ transition: 'fill 0.15s' }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      )}
    </SectionCard>
  );
};

// ─── Main view ────────────────────────────────────────────────────────────

// Matches ContextScoreView's page-level spacing exactly — no horizontal
// padding on desktop (the dashboard layout already provides it, so the
// cards line up with the property header bar instead of double-inset), and
// no extra top padding beyond what the layout puts below that header bar.
// px is driven directly off the isMobile JS state (not a theme breakpoint)
// so it's unambiguous — same reasoning as MOBILE_BREAKPOINT_PX elsewhere.
const pageSx = (isMobile: boolean) =>
  ({
    pt: 0,
    pb: isMobile ? '10px' : '20px',
    px: isMobile ? '10px' : 0,
    maxWidth: isMobile ? 'none' : 1200,
    mx: 'auto',
  }) as const;

export const MarketInsightsView = () => {
  const { selectedProperty } = usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const { data, isLoading, isError } = useMarketInsights(propertyId);
  const isMobile = useIsMobileWidth();

  if (!propertyId) {
    return (
      <Box sx={pageSx(isMobile)}>
        <span style={{ fontSize: 13, color: COLORS.textDim, fontFamily: FONT }}>
          Select a property to see market insights.
        </span>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ ...pageSx(isMobile), display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} sx={{ color: COLORS.accent1 }} />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box sx={pageSx(isMobile)}>
        <span style={{ fontSize: 13, color: COLORS.textDim, fontFamily: FONT }}>
          Couldn't load market data for this property right now. Try again shortly.
        </span>
      </Box>
    );
  }

  return (
    <Box data-testid="market-insights-view" sx={pageSx(isMobile)}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '10px' : '20px' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '10px' : '20px',
          }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <AvmSection data={data} propertyId={propertyId} isMobile={isMobile} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <MarketDynamicsGauge data={data} propertyId={propertyId} />
          </Box>
        </Box>
        <ComparablesSection data={data} propertyId={propertyId} isMobile={isMobile} />
        <TrendsSection data={data} isMobile={isMobile} />
      </Box>
    </Box>
  );
};
